"use client";

import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	type BufferAheadOptions,
	DEFAULT_BUFFER_AHEAD_MS,
	DEFAULT_BUFFER_EDGE_TOLERANCE_MS,
	isAtBufferedBoundary,
	isBufferedAheadOfPosition,
	resolveSegmentDurationMsFromAudio,
	waitForBufferAheadOfPosition,
} from "./buffer-ahead";
import { waitForAudioDuration } from "./interactive-audio-prepare";

export interface EnsureBufferAtPositionOptions {
	resumePlayback?: boolean;
	/** Restore play after buffer when the user was playing before seek/scrub. */
	restorePlaybackIntent?: boolean;
	/**
	 * When true, show blocking wait UI (spinner / disabled controls).
	 * Defaults to true only when the media must wait before playback can proceed.
	 */
	blocking?: boolean;
}

export interface UseInteractiveAudioBufferAheadOptions
	extends BufferAheadOptions {
	audioRef: RefObject<HTMLAudioElement | null>;
	getPositionMs: () => number;
	getSegmentDurationMs?: () => number;
	/** When false, buffer-edge stalls are ignored (e.g. idle transport). */
	getShouldMonitorPlayback?: () => boolean;
}

export interface UseInteractiveAudioBufferAheadResult {
	/** True only while playback is blocked waiting for buffer-ahead at the current position. */
	isBuffering: boolean;
	isBufferedForCurrentPosition: boolean;
	ensureBufferAtPosition: (
		positionMs: number,
		options?: EnsureBufferAtPositionOptions,
	) => Promise<void>;
	checkPlaybackBufferEdge: () => void;
	waitForCurrentPositionBuffer: () => Promise<void>;
	registerResumePlaybackHandler: (handler: (() => void) | null) => void;
}

function resolveSegmentDurationMs(
	audio: HTMLAudioElement,
	getSegmentDurationMs?: () => number,
): number {
	const explicit = getSegmentDurationMs?.();
	if (explicit != null && explicit > 0) {
		return explicit;
	}
	return resolveSegmentDurationMsFromAudio(audio);
}

export function useInteractiveAudioBufferAhead({
	audioRef,
	getPositionMs,
	getSegmentDurationMs,
	getShouldMonitorPlayback,
	minBufferAheadMs = DEFAULT_BUFFER_AHEAD_MS,
	bufferEdgeToleranceMs = DEFAULT_BUFFER_EDGE_TOLERANCE_MS,
}: UseInteractiveAudioBufferAheadOptions): UseInteractiveAudioBufferAheadResult {
	const [isBuffering, setIsBuffering] = useState(false);
	const [isBufferedForCurrentPosition, setIsBufferedForCurrentPosition] =
		useState(false);
	const ensureTokenRef = useRef(0);
	const wasPlayingBeforeStallRef = useRef(false);
	const resumePlaybackRef = useRef<(() => void) | null>(null);

	const bufferOptions: BufferAheadOptions = {
		minBufferAheadMs,
		bufferEdgeToleranceMs,
	};

	const readBufferedState = useCallback(
		(positionMs: number) => {
			const audio = audioRef.current;
			if (!audio?.src) {
				return false;
			}
			const segmentDurationMs = resolveSegmentDurationMs(
				audio,
				getSegmentDurationMs,
			);
			return isBufferedAheadOfPosition(
				audio,
				positionMs,
				segmentDurationMs,
				bufferOptions,
			);
		},
		[audioRef, bufferOptions, getSegmentDurationMs],
	);

	const resumeAfterBlockingWait = useCallback(
		(options: EnsureBufferAtPositionOptions) => {
			if (
				options.restorePlaybackIntent === true ||
				options.resumePlayback === true
			) {
				resumePlaybackRef.current?.();
			}
		},
		[],
	);

	const resolvePlaybackIntent = useCallback(
		(
			audio: HTMLAudioElement,
			options: EnsureBufferAtPositionOptions,
		): boolean => {
			if (options.restorePlaybackIntent === true) {
				return true;
			}
			if (options.restorePlaybackIntent === false) {
				return false;
			}
			if (options.resumePlayback === true) {
				return true;
			}
			return !audio.paused;
		},
		[],
	);

	const ensureBufferAtPosition = useCallback(
		async (
			positionMs: number,
			options: EnsureBufferAtPositionOptions = {},
		) => {
			const audio = audioRef.current;
			if (!audio?.src) {
				setIsBufferedForCurrentPosition(false);
				return;
			}

			if (readBufferedState(positionMs)) {
				setIsBufferedForCurrentPosition(true);
				resumeAfterBlockingWait(options);
				return;
			}

			const shouldBlock = options.blocking ?? true;

			if (!shouldBlock) {
				void waitForAudioDuration(audio).then(async () => {
					const segmentDurationMs = resolveSegmentDurationMs(
						audio,
						getSegmentDurationMs,
					);
					try {
						audio.currentTime = Math.max(0, positionMs / 1000);
					} catch {
						// Ignore seek failures while metadata is still loading.
					}
					await waitForBufferAheadOfPosition(
						audio,
						positionMs,
						segmentDurationMs,
						bufferOptions,
					);
					if (readBufferedState(positionMs)) {
						setIsBufferedForCurrentPosition(true);
					}
				});
				return;
			}

			wasPlayingBeforeStallRef.current = resolvePlaybackIntent(
				audio,
				options,
			);
			if (!audio.paused) {
				audio.pause();
			}

			const token = ensureTokenRef.current + 1;
			ensureTokenRef.current = token;
			setIsBuffering(true);

			try {
				await waitForAudioDuration(audio);
				const segmentDurationMs = resolveSegmentDurationMs(
					audio,
					getSegmentDurationMs,
				);

				try {
					audio.currentTime = Math.max(0, positionMs / 1000);
				} catch {
					// Ignore seek failures while metadata is still loading.
				}

				await waitForBufferAheadOfPosition(
					audio,
					positionMs,
					segmentDurationMs,
					bufferOptions,
				);

				if (ensureTokenRef.current !== token) {
					return;
				}

				setIsBufferedForCurrentPosition(true);
				resumeAfterBlockingWait(options);
			} finally {
				if (ensureTokenRef.current === token) {
					setIsBuffering(false);
				}
			}
		},
		[
			audioRef,
			bufferOptions,
			getSegmentDurationMs,
			readBufferedState,
			resolvePlaybackIntent,
			resumeAfterBlockingWait,
		],
	);

	const waitForCurrentPositionBuffer = useCallback(async () => {
		await ensureBufferAtPosition(getPositionMs(), { blocking: true });
	}, [ensureBufferAtPosition, getPositionMs]);

	const checkPlaybackBufferEdge = useCallback(() => {
		if (getShouldMonitorPlayback?.() === false) {
			return;
		}

		const audio = audioRef.current;
		if (!audio || audio.paused) {
			return;
		}

		const positionMs = getPositionMs();
		const segmentDurationMs = resolveSegmentDurationMs(
			audio,
			getSegmentDurationMs,
		);

		if (
			!isAtBufferedBoundary(audio, positionMs, bufferOptions) ||
			isBufferedAheadOfPosition(
				audio,
				positionMs,
				segmentDurationMs,
				bufferOptions,
			)
		) {
			setIsBufferedForCurrentPosition(
				isBufferedAheadOfPosition(
					audio,
					positionMs,
					segmentDurationMs,
					bufferOptions,
				),
			);
			return;
		}

		void ensureBufferAtPosition(positionMs, {
			blocking: true,
			resumePlayback: true,
		});
	}, [
		audioRef,
		bufferOptions,
		ensureBufferAtPosition,
		getPositionMs,
		getSegmentDurationMs,
		getShouldMonitorPlayback,
	]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio?.src) {
			setIsBufferedForCurrentPosition(false);
			return;
		}

		const syncBufferedState = () => {
			setIsBufferedForCurrentPosition(readBufferedState(getPositionMs()));
		};

		syncBufferedState();
		audio.addEventListener("progress", syncBufferedState);
		audio.addEventListener("loadeddata", syncBufferedState);
		audio.addEventListener("canplay", syncBufferedState);

		return () => {
			audio.removeEventListener("progress", syncBufferedState);
			audio.removeEventListener("loadeddata", syncBufferedState);
			audio.removeEventListener("canplay", syncBufferedState);
		};
	}, [audioRef, getPositionMs, readBufferedState]);

	const registerResumePlaybackHandler = useCallback(
		(handler: (() => void) | null) => {
			resumePlaybackRef.current = handler;
		},
		[],
	);

	return {
		isBuffering,
		isBufferedForCurrentPosition,
		ensureBufferAtPosition,
		checkPlaybackBufferEdge,
		waitForCurrentPositionBuffer,
		registerResumePlaybackHandler,
	};
}
